// @flow
import { isValidElement } from "react";
import ReactDOM from "react-dom";
import { Provider, useSelector } from "react-redux";
import { ThemeProvider } from "styled-components";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import {
    View as OriginalMnView,
    setEnabled
} from "backbone.marionette";
import { ViewMixin } from "extensions/_viewMixin";

import { store } from "common/store";
import { theme } from "common/themes/styled";

export const ReactToMarionetteView = OriginalMnView.extend({
    unmountComponentAtNode() {
        if (this._reactMounted) {
            ReactDOM.unmountComponentAtNode(this.el);
            this._reactMounted = false;
        }
    }
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false, // when a query hits an error, do not retry it by default.
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            staleTime: Number.POSITIVE_INFINITY // Query results should not go stale by default.
        }
    }
});

// This component listens for the `CMD-ALT-SHIFT-i` key combo to show the react-query debugging tool.
function DebuggableQueryClientProvider({ children }) {
    const viewInspector = useSelector((state) => state.settings.viewInspector);
    return (
        <QueryClientProvider client={queryClient}>
            {children}
            {viewInspector && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    );
}

ReactToMarionetteView.setRenderer(function(template, data) {
    if (!this._reactMounted) {
        this.on("destroy", this.unmountComponentAtNode);
        this._reactMounted = true;
    }
    
    let element;
    if (this.getOption("component")) {
        // Create a redux provider
        let component = this.getOption("component");
        if (typeof component === "function") {
            component = component.call(this);
        }
        if (this.getOption("dumb")) {
            element = component;
        } else {
            element = (
                <Provider store={this.getOption("store") || store}>
                    <DebuggableQueryClientProvider>
                        {component}
                    </DebuggableQueryClientProvider>
                </Provider>
            );
        }
        
        element = <ThemeProvider theme={theme}>{element}</ThemeProvider>;
    } else {
        throw new Error("Missing required component option");
    }
    
    if (this.getOption("waitForAttach")) {
        this.once("dom:refresh", () => {
            ReactDOM.render(element, this.el);
        });
    } else {
        ReactDOM.render(element, this.el);
    }
});

setEnabled("DEV_MODE", window.debug);

// Monitor a view's state and abort any requests that are still pending
function abortRequestsOnViewDestroy(view: View) {
    if (view._areRequestEventsMonitored || view.monitorRequestLifecycle === false) {
        return;
    }
    
    view._areRequestEventsMonitored = true;
    view._requests = {};
    
    view.on("destroy", () => {
        abortPendingRequests(view);
        
        // Remove references to abort controllers
        delete view._requests;
    });
}

function abortPendingRequests(view: View) {
    Object.entries(view._requests).forEach(([url, abortController]) => {
        abortController.abort();
    });
}

function unsubscribeReduxWatch(view: View) {
    if (view._isReduxWatchMonitored) {
        return;
    }
    
    view._isReduxWatchMonitored = true;
    view._reduxSubscriptions = [];
    
    view.on("before:destroy", () => {
        view._reduxSubscriptions.forEach((unsubscribe) => unsubscribe());
    
        // Remove references to subscriptions
        delete view._reduxSubscriptions;
    });
}

// Extend with React/Redux mixin
export const View = OriginalMnView.extend({
    constructor: function constructor() {
        // Run regular constructor
        OriginalMnView.prototype.constructor.apply(this, arguments);
    
        abortRequestsOnViewDestroy(this);
        
        unsubscribeReduxWatch(this);
    },
    
    showChildView(region, view, options = {}) {
        if (isValidElement(view)) {
            OriginalMnView.prototype.showChildView.call(this, region, new ReactToMarionetteView({ component: view, ...options }));
        } else {
            OriginalMnView.prototype.showChildView.call(this, region, view);
        }
    },
    
    abortPendingRequests() {
        abortPendingRequests(this);
    },
    
    ...ViewMixin
});
